import { StoneColor } from '../types';
/**
 * 形势判断（简化数子法）
 * 得分 = 己方在盘棋子数 + 己方围住的空点（领地）
 * 输入 stones 为 StoneColor[][]（y 行 x 列），输出黑/白各占的子数。
 *
 * deadStones：终局双方确认的死子坐标（仍留在盘上但已无气/被围死），
 * 计算时先按空点处理（等同于「提掉后再数」），用于修正数子误判。
 */
export function estimateScore(stones, deadStones) {
    const size = stones.length;
    // 死子先提掉（按空点处理）
    const working = stones.map((row) => [...row]);
    if (deadStones && deadStones.length > 0) {
        const dead = new Set(deadStones.map((c) => `${c.x},${c.y}`));
        for (let y = 0; y < size; y++) {
            for (let x = 0; x < size; x++) {
                if (dead.has(`${x + 1},${y + 1}`)) {
                    working[y][x] = StoneColor.EMPTY;
                }
            }
        }
    }
    // flood fill 领地：每个空区域若只接触一种颜色，则该区域归该颜色
    const visited = Array.from({ length: size }, () => new Array(size).fill(false));
    let blackTerritory = 0;
    let whiteTerritory = 0;
    const dirs = [
        [0, 1], [0, -1], [1, 0], [-1, 0]
    ];
    for (let y = 0; y < size; y++) {
        for (let x = 0; x < size; x++) {
            if (working[y][x] !== StoneColor.EMPTY || visited[y][x])
                continue;
            const queue = [[x, y]];
            visited[y][x] = true;
            let area = 0;
            let touchesBlack = false;
            let touchesWhite = false;
            while (queue.length > 0) {
                const [cx, cy] = queue.shift();
                area++;
                for (const [dx, dy] of dirs) {
                    const nx = cx + dx;
                    const ny = cy + dy;
                    if (nx < 0 || nx >= size || ny < 0 || ny >= size)
                        continue;
                    const c = working[ny][nx];
                    if (c === StoneColor.BLACK) {
                        touchesBlack = true;
                        continue;
                    }
                    if (c === StoneColor.WHITE) {
                        touchesWhite = true;
                        continue;
                    }
                    if (!visited[ny][nx]) {
                        visited[ny][nx] = true;
                        queue.push([nx, ny]);
                    }
                }
            }
            if (touchesBlack && !touchesWhite)
                blackTerritory += area;
            else if (touchesWhite && !touchesBlack)
                whiteTerritory += area;
        }
    }
    let blackStones = 0;
    let whiteStones = 0;
    for (let y = 0; y < size; y++) {
        for (let x = 0; x < size; x++) {
            const c = working[y][x];
            if (c === StoneColor.BLACK)
                blackStones++;
            else if (c === StoneColor.WHITE)
                whiteStones++;
        }
    }
    return {
        black: blackStones + blackTerritory,
        white: whiteStones + whiteTerritory
    };
}
/**
 * 自动识别死子（粗略启发式，用于终局自动数子兜底）
 *
 * 判定规则：一块棋若没有任何「己方眼 / 自由气」——即其所有气点所在的空区域都接触敌方
 * （被对方完全围死、逃不出去），则判为死子。
 *
 * 局限：只判「被完全围死」的孤棋；单眼假活、对杀、双活等复杂死活不识别（需人工确认）。
 * 适合终局时把明显已死的孤棋自动提掉，修正数子误判。
 */
export function findDeadStones(stones) {
    const size = stones.length;
    const dirs = [
        [0, 1], [0, -1], [1, 0], [-1, 0]
    ];
    const dead = [];
    const groupVisited = Array.from({ length: size }, () => new Array(size).fill(false));
    for (let y = 0; y < size; y++) {
        for (let x = 0; x < size; x++) {
            const c = stones[y][x];
            if (c === StoneColor.EMPTY || groupVisited[y][x])
                continue;
            const enemy = c === StoneColor.BLACK ? StoneColor.WHITE : StoneColor.BLACK;
            // 1. flood fill 找该连通块 + 气点
            const queue = [[x, y]];
            groupVisited[y][x] = true;
            const group = [];
            const libertySet = new Set();
            while (queue.length > 0) {
                const [cx, cy] = queue.pop();
                group.push({ x: cx + 1, y: cy + 1 });
                for (const [dx, dy] of dirs) {
                    const nx = cx + dx;
                    const ny = cy + dy;
                    if (nx < 0 || nx >= size || ny < 0 || ny >= size)
                        continue;
                    const nc = stones[ny][nx];
                    if (nc === c) {
                        if (!groupVisited[ny][nx]) {
                            groupVisited[ny][nx] = true;
                            queue.push([nx, ny]);
                        }
                    }
                    else if (nc === StoneColor.EMPTY) {
                        libertySet.add(`${nx},${ny}`);
                    }
                }
            }
            if (libertySet.size === 0)
                continue; // 无气（异常），不判死
            // 2. 对气点 flood fill 空区域，判断是否有「眼 / 自由气」（不接触敌方的空区域）
            const regionVisited = new Set();
            let hasEyeOrFree = false;
            for (const key of Array.from(libertySet)) {
                if (regionVisited.has(key))
                    continue;
                const [lx, ly] = key.split(',').map(Number);
                const q = [[lx, ly]];
                regionVisited.add(`${lx},${ly}`);
                let touchesEnemy = false;
                let area = 0;
                while (q.length > 0) {
                    const [ax, ay] = q.pop();
                    area++;
                    for (const [dx, dy] of dirs) {
                        const nx = ax + dx;
                        const ny = ay + dy;
                        if (nx < 0 || nx >= size || ny < 0 || ny >= size)
                            continue;
                        const nc = stones[ny][nx];
                        if (nc === enemy) {
                            touchesEnemy = true;
                        }
                        else if (nc === StoneColor.EMPTY) {
                            const k = `${nx},${ny}`;
                            if (!regionVisited.has(k)) {
                                regionVisited.add(k);
                                q.push([nx, ny]);
                            }
                        }
                        // nc === c（己方）不阻断空区域
                    }
                }
                // 不接触敌方（眼/自由气），或区域够大（稀疏局面的开阔空间，等于有出路）→ 视为活
                if (!touchesEnemy || area > 8)
                    hasEyeOrFree = true;
            }
            // 3. 无眼且无自由气（所有气都被敌方围住）→ 死子
            if (!hasEyeOrFree) {
                dead.push(...group);
            }
        }
    }
    return dead;
}
//# sourceMappingURL=score.js.map